import { AfterViewInit, Component, ViewEncapsulation, OnInit, HostListener, ElementRef, ViewChild } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { InvoiceService } from 'src/app/services/invoice/invoice.service';
import { Invoice } from 'src/app/shared/models/invoice.interface';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { ClientService } from 'src/app/services/user/client.service';
import { UserService } from 'src/app/services/user/user.service';
import { ShipService } from 'src/app/services/ship/ship.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { PortService } from 'src/app/services/port/port.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { USER_TYPES } from 'src/app/config/system.config';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
declare var KTMenu: any;
declare var bootstrap: any;

@Component({
  selector: 'app-invoice-list',
  templateUrl: './invoice-list.component.html',
  styleUrls: ['./invoice-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class InvoiceListComponent extends DestructorComponent implements OnInit, AfterViewInit {

  @ViewChild('formatDropdownContainer') formatDropdownContainer!: ElementRef;

  // Data properties
  invoices: Invoice[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  user:any;
  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  selectedStatus: string = '';
  private searchTimeout: any;

  // Invoice selection properties
  selectedInvoices: Set<string> = new Set();
  selectedPdfFormats: string[] = [];
  showPdfOptions: boolean = false;
  showFormatDropdown: boolean = false;

  // Filter properties
  showFilters: boolean = false;
  filters: any = {
    invoice_number: '',
    stock_number: '',
    chassis_no: '',
    voyage_no: '',
    proforma_from_date: '',
    proforma_to_date: '',
    invoice_from_date: '',
    invoice_to_date: '',
    sailing_from_date: '',
    sailing_to_date: '',
    arrival_from_date: '',
    arrival_to_date: '',
    customer_id: '',
    consignee_id: '',
    ship_id: '',
    sailing_port: '',
    destination_country: '',
    destination_country_label:'',
    destination_port: '',
    keywords: '',
    sales_person_id: '',
    sold: 'all',
    contract: 'all'
  };

  // Customer search properties
  customerSearchTerm: string = '';
  customerSearchResults: any[] = [];
  isCustomerSearching: boolean = false;
  showCustomerDropdown: boolean = false;
  selectedCustomer: any = null;
  private customerSearchSubject = new Subject<string>();

  // Sales Person search properties
  salesPersonSearchTerm: string = '';
  salesPersonSearchResults: any[] = [];
  isSalesPersonSearching: boolean = false;
  showSalesPersonDropdown: boolean = false;
  selectedSalesPersons: any = null;
  private salesPersonSearchSubject = new Subject<string>();

  // Ship Name search properties
  shipSearchTerm: string = '';
  shipSearchResults: any[] = [];
  isShipSearching: boolean = false;
  showShipDropdown: boolean = false;
  selectedShip: any = null;
  private shipSearchSubject = new Subject<string>();

  // Countries and Ports
  countries: any[] = [];
  ports: any[] = [];
  isLoadingPorts: boolean = false;

  // Multi-row editing properties
  hasChanges: boolean = false;
  changedRows: any[] = [];
  formData: { [invoiceId: string]: any } = {};

  // Export properties
  isExportingExcel: boolean = false;

  // Available PDF formats
  pdfFormats = [
    { value: 'plain_invoice', label: 'Imperial Solutions Format' },
    { value: 'caricom_invoice', label: 'Caricom Invoice Format' },
    { value: 'commercial_invoice', label: 'Commercial Invoice Format' },
    { value: 'standard_invoice', label: 'Dummy Invoice Format' },
    { value: 'shipping_information', label: 'Shipping Instrcution Format' },
    { value: 'export_certificate', label: 'Export Certificate Format' },
    { value: 'house_bill_landing', label: 'House Bill of Lading Format' },
    { value: 'ctn_invoice', label: 'CTN Invoice' },
    { value: 'ctn_invoice_excel', label: 'CTN Invoice Excel' },
    { value: 'certificate_of_origin', label: 'Certificate of Origin' },
    { value: 'insurance_invoice', label: 'Insurance Invoice' }
  ];

  // Available status options for tabs
  statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'unapproved', label: 'Un Approved' },
    { value: 'complete', label: 'Complete' }
  ];

  constructor(
    private invoiceService: InvoiceService,
    private clientService: ClientService,
    private userService: UserService,
    private shipService: ShipService,
    private globalStateService: GlobalStateService,
    private portService: PortService,
    private generalService: GeneralService,
    private _router: Router
  ) {
    super();
    this.setupCustomerSearchDebounce();
    this.setupSalesPersonSearchDebounce();
    this.setupShipSearchDebounce();
  }

  ngOnInit(): void {
    this.loadInvoices(1,'',{status:''});
    this.loadCountries();
  }

  ngAfterViewInit(): void {
    // Don't initialize DataTables since we're using Angular for data management
    // this.datatableService.init('kt_table_invoices');

    // Initialize menus after view is initialized
    this.initializeMenus();
    this.subscriptions.push(
      this.globalStateService.user.subscribe(user => {
        this.user = user;
      })
    );
  }

  loadInvoices(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    filters.status = this.selectedStatus;

    for(const key in filters) {
      if (filters[key] === '' || filters[key] === null) {
        delete filters[key];
      }
    }

    this.subscriptions.push(
      this.invoiceService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<Invoice>) => {
          if (response.status && response.data) {
            this.invoices = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
            // Initialize form data after loading invoices
            this.initializeFormData();
          } else {
            this.invoices = [];
            this.resetPagination();
          }
          this.isLoading = false;
          // Initialize KTMenu instances after data is loaded
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading invoices:', error);
          this.invoices = [];
          this.resetPagination();
          this.isLoading = false;
          // Initialize KTMenu instances even on error
          this.refreshMenus();
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    this.loadInvoices(page, this.searchTerm);
  }

  onSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.loadInvoices(1, searchTerm);
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Debounce search by 300ms
    this.searchTimeout = setTimeout(() => {
      this.onSearch(target.value);
    }, 300);
  }

  onStatusFilter(status: string): void {
    this.selectedStatus = status;
    const filters: Record<string, any> = {};
    if (status && status !== 'all') {
      filters['status'] = status;
    }
    this.loadInvoices(1, this.searchTerm, filters);
  }

  onResetFilters(): void {
    this.searchTerm = '';
    this.selectedStatus = '';
    this.loadInvoices(1);
  }

  // Invoice selection methods
  onInvoiceSelect(invoiceId: string, event: any): void {
    if (event.target.checked) {
      this.selectedInvoices.add(invoiceId);
    } else {
      this.selectedInvoices.delete(invoiceId);
    }
    this.updatePdfOptionsVisibility();
  }

  onSelectAll(event: any): void {
    if (event.target.checked) {
      this.invoices.forEach(invoice => this.selectedInvoices.add(invoice._id));
    } else {
      this.selectedInvoices.clear();
    }
    this.updatePdfOptionsVisibility();
  }

  isInvoiceSelected(invoiceId: string): boolean {
    return this.selectedInvoices.has(invoiceId);
  }

  isAllSelected(): boolean {
    return this.invoices.length > 0 && this.selectedInvoices.size === this.invoices.length;
  }

  isIndeterminate(): boolean {
    return this.selectedInvoices.size > 0 && this.selectedInvoices.size < this.invoices.length;
  }

  private updatePdfOptionsVisibility(): void {
    this.showPdfOptions = this.selectedInvoices.size > 0;
    if (!this.showPdfOptions) {
      this.selectedPdfFormats = [];
    }
  }

  onPdfFormatToggle(formatValue: string): void {
    const index = this.selectedPdfFormats.indexOf(formatValue);
    if (index > -1) {
      this.selectedPdfFormats.splice(index, 1);
    } else {
      this.selectedPdfFormats.push(formatValue);
    }
    console.log('Selected formats:', this.selectedPdfFormats);
  }

  isFormatSelected(formatValue: string): boolean {
    return this.selectedPdfFormats.includes(formatValue);
  }

  toggleFormatDropdown(): void {
    this.showFormatDropdown = !this.showFormatDropdown;
  }

  closeFormatDropdown(): void {
    this.showFormatDropdown = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showFormatDropdown && this.formatDropdownContainer) {
      const clickedInside = this.formatDropdownContainer.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.closeFormatDropdown();
      }
    }
  }

  onPreviewPdf(): void {
    if (this.selectedInvoices.size === 0 || this.selectedPdfFormats.length === 0) {
      return;
    }

    const selectedInvoiceIds = Array.from(this.selectedInvoices);
    const selectedInvoiceData = this.invoices.filter(invoice =>
      selectedInvoiceIds.includes(invoice._id)
    );

    // Validate that all selected invoices have the same buyer_id and ship._id
    if (selectedInvoiceData.length > 1) {
      const firstInvoice = selectedInvoiceData[0];
      const firstBuyerId = firstInvoice.order?.buyer_id;
      const firstShipId = firstInvoice.ship?._id;

      // Check if all invoices have the same buyer_id
      const allSameBuyer = selectedInvoiceData.every(invoice =>
        invoice.order?.buyer_id === firstBuyerId
      );

      if (!allSameBuyer) {
        Swal.fire({
          text: 'Selected invoices are not from the same buyer',
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn fw-bold btn-primary",
          }
        });
        return;
      }

      // Check if all invoices have the same ship._id
      const allSameShip = selectedInvoiceData.every(invoice =>
        invoice.ship?._id === firstShipId
      );

      if (!allSameShip) {
        Swal.fire({
          text: 'Selected invoices are not from the same ship',
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn fw-bold btn-primary",
          }
        });
        return;
      }
    }

    // Set the modal data
    this.selectedInvoicesForModal = selectedInvoiceData;
    this.selectedFormatsForModal = [...this.selectedPdfFormats];

    console.log('Modal data prepared:', {
      invoices: this.selectedInvoicesForModal.length,
      formats: this.selectedFormatsForModal
    });

    // Open the modal programmatically after validation passes
    const modalElement = document.getElementById('kt_modal_view_invoice');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  // Modal integration properties
  selectedInvoicesForModal: Invoice[] = [];
  selectedFormatsForModal: string[] = [];

  // Invoice detail modal properties
  selectedInvoiceForDetail: Invoice | null = null;
  selectedInvoiceIdForDetail: string = '';



  getSelectedCount(): number {
    return this.selectedInvoices.size;
  }

  onClearSelection(): void {
    this.selectedInvoices.clear();
    this.updatePdfOptionsVisibility();
  }

  // Helper methods for displaying data
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatCurrency(amount: number, currency: string | undefined = 'USD'): string {
    if (!amount) return '-';
    return `${amount.toLocaleString()} ${currency || 'USD'}`;
  }

  calculatePaidPercentage(paid: number, total: number): string {
    if (!total || total === 0) return '0%';
    const percentage = (paid / total) * 100;
    return `${Math.round(percentage)}%`;
  }

  trackByInvoiceId(_index: number, invoice: Invoice): string {
    return invoice._id;
  }

  // Pagination helper methods
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    const currentPage = this.paginationInfo.currentPage;
    const lastPage = this.paginationInfo.lastPage;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(lastPage, startPage + maxPagesToShow - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  // Math helper for template
  get Math() {
    return Math;
  }

  // Initialize KTMenu instances
  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Refresh menus after data load
  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 50);
  }

  invoiceStatus(invoiceId: string, status: string) {


    Swal.fire({
      title: `${status=="approved"?"Un Approve":"Approve"} Invoice?`,
      text: `Are you sure you want to ${status=="approved"?"un approve":"approve"} this invoice.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${status=="approved"?"un approve":"approve"} invoice!`,
      cancelButtonText: 'No, cancel',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-primary',
        cancelButton: 'btn fw-bold btn-active-light-primary',
      },
    }).then((result) => {
      if (result.isConfirmed) {

        status = status=="approved"?"pending":"approved";
        this.invoiceService.UpdateData(invoiceId, { status }).subscribe({
          next: (response: any) => {
            if (response.status) {
              this.loadInvoices(this.currentPage);
            }
          },
          error: (error) => {
            console.error('Error updating invoice status:', error);
          }
        });
      }
    });



  }

  // Method to open invoice detail modal
  openInvoiceDetailModal(invoice: Invoice): void {
    this.selectedInvoiceForDetail = invoice;
    this.selectedInvoiceIdForDetail = invoice._id;

    // Open the modal using Bootstrap
    const modalElement = document.getElementById('kt_modal_invoice_detail');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  // Method to handle invoice cancelled event
  onInvoiceCancelled(): void {
    console.log('Invoice cancelled, refreshing list...');
    this.loadInvoices(this.currentPage, this.searchTerm);
  }

  // Method to handle invoice updated event
  onInvoiceUpdated(): void {
    this.loadInvoices(this.currentPage, this.searchTerm);
  }

  // Filter methods
  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  applyFilters(): void {
    // Log selected customer and sales person values
    if (this.selectedCustomer) {
      console.log('Selected Customer Filter:', this.selectedCustomer);
      this.filters.customer_id = this.selectedCustomer._id;
    }
    if (this.selectedSalesPersons) {
      console.log('Selected Sales Person Filter:', this.selectedSalesPersons);
      this.filters.sales_person_id = this.selectedSalesPersons._id;
    }
    if (this.selectedShip) {
      console.log('Selected Ship Filter:', this.selectedShip);
      this.filters.ship_id = this.selectedShip._id;
    }

    // Handle radio button filters - don't send if 'all' is selected
    const apiFilters = { ...this.filters };
    if (apiFilters.sold === 'all') {
      delete apiFilters.sold;
    }
    if (apiFilters.contract === 'all') {
      delete apiFilters.contract;
    }

    console.log("filters-->", apiFilters);
    this.loadInvoices(1, this.searchTerm, apiFilters);
  }

  clearFilters(): void {
    this.filters = {
      invoice_number: '',
      stock_number: '',
      chassis_no: '',
      voyage_no: '',
      proforma_from_date: '',
      proforma_to_date: '',
      invoice_from_date: '',
      invoice_to_date: '',
      sailing_from_date: '',
      sailing_to_date: '',
      arrival_from_date: '',
      arrival_to_date: '',
      customer_id: '',
      consignee_id: '',
      ship_id: '',
      sailing_port: '',
      destination_country: '',
      destination_country_label:'',
      destination_port: '',
      keywords: '',
      sales_person_id: '',
      sold: 'all',
      contract: 'all'
    };

    // Clear search terms and selections
    this.customerSearchTerm = '';
    this.selectedCustomer = null;
    this.customerSearchResults = [];
    this.salesPersonSearchTerm = '';
    this.selectedSalesPersons = null;
    this.salesPersonSearchResults = [];
    this.shipSearchTerm = '';
    this.selectedShip = null;
    this.shipSearchResults = [];
    this.ports = [];

    this.loadInvoices(1, this.searchTerm, {});
  }

  hasActiveFilters(): boolean {
    return Object.keys(this.filters).some(key => {
      const value = this.filters[key];
      if (key === 'sold' || key === 'contract') {
        return value !== 'all';
      }
      return value !== '' && value !== null && value !== undefined;
    });
  }

  getActiveFiltersCount(): number {
    return Object.keys(this.filters).filter(key => {
      const value = this.filters[key];
      if (key === 'sold' || key === 'contract') {
        return value !== 'all';
      }
      return value !== '' && value !== null && value !== undefined;
    }).length;
  }

  // Load countries method
  loadCountries(): void {
    this.subscriptions.push(
      this.globalStateService.GetCountries().subscribe({
        next: (countries: any) => {
          this.countries = countries || [];
        },
        error: (error: any) => {
          console.error('Error loading countries:', error);
        }
      })
    );
  }

  // Country change handler for cascading port dropdown
  onDestinationCountryChange(event: any): void {
    const countryCode = event.target.value;
    this.filters.destination_country = countryCode;
    this.filters.destination_country_label = event.target.options[event.target.selectedIndex].text;
    this.filters.destination_port = ''; // Reset port selection
    this.ports = []; // Clear ports

    if (countryCode) {
      this.loadPorts(countryCode);
    }
  }

  // Load ports based on country
  loadPorts(countryCode: string): void {
    this.isLoadingPorts = true;
    this.subscriptions.push(
      this.portService.GetData('1', '', { country_code: countryCode, paginate: false }).subscribe({
        next: (response: any) => {
          this.isLoadingPorts = false;
          if (response.status && response.data) {
            this.ports = response.data.data || response.data || [];
          } else {
            this.ports = [];
          }
        },
        error: (error: any) => {
          this.isLoadingPorts = false;
          console.error('Error loading ports:', error);
          this.ports = [];
        }
      })
    );
  }

  // Customer search methods
  private setupCustomerSearchDebounce(): void {
    this.customerSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchCustomers(searchTerm);
    });
  }

  onCustomerSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.customerSearchTerm = target.value;
    this.selectedCustomer = null;
    this.customerSearchSubject.next(this.customerSearchTerm);
    this.showCustomerDropdown = true;
  }

  onCustomerInputFocus(): void {
    if (this.customerSearchResults.length > 0) {
      this.showCustomerDropdown = true;
    }
  }

  onCustomerInputBlur(): void {
    setTimeout(() => {
      this.showCustomerDropdown = false;
    }, 200);
  }

  private searchCustomers(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.customerSearchResults = [];
      this.showCustomerDropdown = false;
      return;
    }

    this.isCustomerSearching = true;
    this.subscriptions.push(
      this.clientService.GetData('1', searchTerm).subscribe({
        next: (response: any) => {
          this.isCustomerSearching = false;
          if (response.status && response.data && response.data.data) {
            this.customerSearchResults = response.data.data;
            this.showCustomerDropdown = this.customerSearchResults.length > 0;
          } else {
            this.customerSearchResults = [];
            this.showCustomerDropdown = false;
          }
        },
        error: (error: any) => {
          this.isCustomerSearching = false;
          console.error('Error searching customers:', error);
          this.customerSearchResults = [];
          this.showCustomerDropdown = false;
        }
      })
    );
  }

  selectCustomer(customer: any): void {
    this.selectedCustomer = customer;
    this.customerSearchTerm = this.getCustomerDisplayName(customer);
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];
  }

  getCustomerDisplayName(customer: any): string {
    if (customer.name && customer.company) {
      return `${customer.name}, ${customer.company}`;
    }
    return `${customer.name || ''} ${customer.last_name || ''}`.trim() || customer.email || 'Unknown Customer';
  }

  // Sales Person search methods
  private setupSalesPersonSearchDebounce(): void {
    this.salesPersonSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchSalesPersons(searchTerm);
    });
  }

  onSalesPersonSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.salesPersonSearchTerm = target.value;
    this.selectedSalesPersons = null;
    this.salesPersonSearchSubject.next(this.salesPersonSearchTerm);
    this.showSalesPersonDropdown = true;
  }

  onSalesPersonInputFocus(): void {
    if (this.salesPersonSearchResults.length > 0) {
      this.showSalesPersonDropdown = true;
    }
  }

  onSalesPersonInputBlur(): void {
    setTimeout(() => {
      this.showSalesPersonDropdown = false;
    }, 200);
  }

  private searchSalesPersons(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.salesPersonSearchResults = [];
      this.showSalesPersonDropdown = false;
      return;
    }

    this.isSalesPersonSearching = true;
    this.subscriptions.push(
      this.userService.GetData('1', searchTerm, { role_type: USER_TYPES.ADMIN, paginate: false }).subscribe({
        next: (response: any) => {
          this.isSalesPersonSearching = false;
          if (response.status && response.data) {
            const salesPersons = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.salesPersonSearchResults = salesPersons;
            this.showSalesPersonDropdown = this.salesPersonSearchResults.length > 0;
          } else {
            this.salesPersonSearchResults = [];
            this.showSalesPersonDropdown = false;
          }
        },
        error: (error: any) => {
          this.isSalesPersonSearching = false;
          console.error('Error searching sales persons:', error);
          this.salesPersonSearchResults = [];
          this.showSalesPersonDropdown = false;
        }
      })
    );
  }

  selectSalesPersons(salesPerson: any): void {
    this.selectedSalesPersons = salesPerson;
    this.salesPersonSearchTerm = this.getSalesPersonDisplayName(salesPerson);
    this.showSalesPersonDropdown = false;
    this.salesPersonSearchResults = [];
  }

  getSalesPersonDisplayName(salesPerson: any): string {
    if (salesPerson.name && salesPerson.company) {
      return `${salesPerson.name}, ${salesPerson.company}, ${salesPerson.currency_type}`;
    }
    return `${salesPerson.name || ''} ${salesPerson.last_name || ''}`.trim() || salesPerson.email || 'Unknown Sales Person';
  }

  // Ship search methods
  private setupShipSearchDebounce(): void {
    this.shipSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchShips(searchTerm);
    });
  }

  onShipSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.shipSearchTerm = target.value;
    this.selectedShip = null;
    this.shipSearchSubject.next(this.shipSearchTerm);
    this.showShipDropdown = true;
  }

  onShipInputFocus(): void {
    if (this.shipSearchResults.length > 0) {
      this.showShipDropdown = true;
    }
  }

  onShipInputBlur(): void {
    setTimeout(() => {
      this.showShipDropdown = false;
    }, 200);
  }

  private searchShips(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.shipSearchResults = [];
      this.showShipDropdown = false;
      return;
    }

    this.isShipSearching = true;
    this.subscriptions.push(
      this.shipService.GetData('1', searchTerm, { paginate: false }).subscribe({
        next: (response: any) => {
          this.isShipSearching = false;
          if (response.status && response.data) {
            this.shipSearchResults = response.data.data || response.data || [];
            this.showShipDropdown = this.shipSearchResults.length > 0;
          } else {
            this.shipSearchResults = [];
            this.showShipDropdown = false;
          }
        },
        error: (error: any) => {
          this.isShipSearching = false;
          console.error('Error searching ships:', error);
          this.shipSearchResults = [];
          this.showShipDropdown = false;
        }
      })
    );
  }

  selectShip(ship: any): void {
    this.selectedShip = ship;
    this.shipSearchTerm = this.getShipDisplayName(ship);
    this.showShipDropdown = false;
    this.shipSearchResults = [];
  }

  getShipDisplayName(ship: any): string {
    return `${ship.ship_id}, ${ship.name} - ${ship.company} (${ship.voyage_no})`;
  }

  // Copy selected ISL numbers to clipboard
  copySelectedISL(): void {
    if (this.selectedInvoices.size === 0) {
      return;
    }

    // Get car_no values for selected invoices
    const selectedInvoiceIds = Array.from(this.selectedInvoices);
    const selectedCarNos = this.invoices
      .filter(invoice => selectedInvoiceIds.includes(invoice._id))
      .map(invoice => invoice.vehicle?.car_no)
      .filter(carNo => carNo) // Remove empty values
      .join(',');

    if (selectedCarNos) {
      // Copy to clipboard
      navigator.clipboard.writeText(selectedCarNos).then(() => {
        // Show success message
        console.log('ISL numbers copied to clipboard:', selectedCarNos);
        alert(`${this.selectedInvoices.size} ISL numbers copied to clipboard!`);
      }).catch(err => {
        console.error('Failed to copy to clipboard:', err);
        alert('Failed to copy to clipboard. Please try again.');
      });
    } else {
      alert('No ISL numbers found for selected invoices.');
    }
  }

  // Open vehicle document upload modal
  onDocsClick(vehicleId: string, event: Event): void {
    // Stop event propagation to prevent row click
    event.stopPropagation();

    console.log('Docs clicked for vehicle:', vehicleId);

    if (vehicleId) {
      // Find the vehicle object from the invoice
      const invoice = this.invoices.find(inv => inv.vehicle?._id === vehicleId);
      const vehicle = invoice?.vehicle;

      console.log('Found invoice:', invoice);
      console.log('Found vehicle:', vehicle);

      if (vehicle) {
        // Ensure the vehicle object has all required properties for the modal
        const vehicleForModal = {
          ...vehicle,
          // Map properties to what the modal expects
          stock_no: vehicle.car_no || vehicle.inventory_no,
          chassis_no: vehicle.vin_no || vehicle.chassis_no,
          // Ensure these properties exist
          make: vehicle.make || vehicle.company_name || '',
          model: vehicle.model || vehicle.name || '',
          year: vehicle.year || '',
          pictures: vehicle.pictures || []
        };

        console.log('Vehicle for modal:', vehicleForModal);

        // Set the selected vehicle for the modal
        (window as any).selectedVehicleForDocuments = vehicleForModal;

        // Open the vehicle upload documents modal
        const modalElement = document.getElementById('kt_modal_vehicle_upload_documents');
        if (modalElement) {
          try {
            const modal = new bootstrap.Modal(modalElement);
            modal.show();
            console.log('Vehicle upload documents modal opened successfully');
          } catch (error) {
            console.error('Error opening vehicle upload documents modal:', error);
          }
        } else {
          console.error('Vehicle upload documents modal element not found');
        }
      } else {
        console.error('Vehicle not found for ID:', vehicleId);
      }
    }
  }

  // Form data management methods
  private initializeFormData(): void {
    this.formData = {};
    this.invoices.forEach(invoice => {
      this.formData[invoice._id] = {
        courier_no: invoice.courier_no || '',
        re_auction: invoice.re_auction || false
      };
    });
    // Reset changes when data is reloaded
    this.changedRows = [];
    this.hasChanges = false;
  }

  // Helper methods for form data access
  getFormValue(invoiceId: string, field: string): any {
    return this.formData[invoiceId]?.[field] || '';
  }

  setFormValue(invoiceId: string, field: string, value: any): void {
    if (!this.formData[invoiceId]) {
      this.formData[invoiceId] = {};
    }
    this.formData[invoiceId][field] = value;
    this.onFieldChange(invoiceId, field, value);
  }

  onFieldChange(invoiceId: string, field: string, value: any): void {
    if (!this.formData[invoiceId]) {
      this.formData[invoiceId] = {};
    }

    this.formData[invoiceId][field] = value;

    // Check if this invoice is already in changed rows
    const existingIndex = this.changedRows.findIndex(row => row.invoice_id === invoiceId);

    if (existingIndex >= 0) {
      // Update existing changed row
      this.changedRows[existingIndex] = {
        invoice_id: invoiceId,
        courier_no: this.formData[invoiceId].courier_no,
        re_auction: this.formData[invoiceId].re_auction
      };
    } else {
      // Add new changed row
      this.changedRows.push({
        invoice_id: invoiceId,
        courier_no: this.formData[invoiceId].courier_no,
        re_auction: this.formData[invoiceId].re_auction
      });
    }

    this.hasChanges = this.changedRows.length > 0;
  }

  onSave(): void {
    if (this.changedRows.length === 0) {
      return;
    }

    const changedCount = this.changedRows.length;

    console.log('Saving changes for invoices:', this.changedRows);

    // Show loading state
    this.isLoading = true;

    // Call the UpdateDataValue method with the changed rows
    this.subscriptions.push(
      this.invoiceService.UpdateDataValue({ data: this.changedRows }).subscribe({
        next: (response: any) => {
          console.log('Update response:', response);
          this.isLoading = false;

          if (response.status) {
            // Success - show success message
            Swal.fire({
              text: `Successfully updated ${changedCount} invoice(s)!`,
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-primary",
              }
            }).then(() => {
              // Reset changes
              this.changedRows = [];
              this.hasChanges = false;
              // Reload invoices to get updated data
              this.loadInvoices(this.currentPage, this.searchTerm);
            });
          } else {
            // Error - show error message
            Swal.fire({
              text: response.message || 'Failed to update invoices.',
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-primary",
              }
            });
          }
        },
        error: (error: any) => {
          console.error('Error updating invoices:', error);
          this.isLoading = false;
          Swal.fire({
            text: 'An error occurred while updating invoices.',
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-primary",
            }
          });
        }
      })
    );
  }

  // Export Excel method
  exportExcel(): void {
    // Set loading state
    this.isExportingExcel = true;

    // Prepare filters object
    const filtersObj = this.hasActiveFilters() ? { ...this.filters } : {};

    // Add type parameter
    filtersObj.invoice_type = 'invoice-list';
    filtersObj.type = 'invoice';
    filtersObj.excel_name = "Invoice List"
    filtersObj.status = this.selectedStatus;

    console.log('Exporting Excel with filters:', filtersObj);

    // Call the generateExcelPost method
    this.subscriptions.push(
      this.generalService.generateExcelPost(filtersObj).subscribe({
        next: (response: any) => {
          this.isExportingExcel = false;
          console.log('Excel export response:', response);

          if (response.status && response.data && response.data.url) {
            // Open the Excel file URL in a new tab
            window.open(response.data.url, '_blank');
          } else {
            // Show error message if no URL is returned
            Swal.fire({
              text: 'Failed to generate Excel file. No URL received.',
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-primary",
              }
            });
          }
        },
        error: (error: any) => {
          this.isExportingExcel = false;
          console.error('Error exporting Excel:', error);
          Swal.fire({
            text: 'An error occurred while exporting Excel file.',
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-primary",
            }
          });
        }
      })
    );
  }

  viewDetailClient(client_id: any): void {
    if (client_id) {
      const url = this._router.serializeUrl(
        this._router.createUrlTree(['sales/client-manager', client_id])
      );

      window.open(url, '_blank');
    }
  }

}
